import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../api/axios";
import { API_ORIGIN } from "../api/apiConfig";
import AdminLoadingOverlay from "../components/AdminLoadingOverlay";
import AdminShell from "../components/AdminShell";
import { useAuth } from "../context/AuthContext";
import "../styles/admin.css";

const initialForm = {
  firstName: "",
  lastName: "",
  contactNumber: "",
  status: "available",
  licenseNumber: "",
  licenseExpiryDate: "",
  profilePhoto: "",
  licenseFrontImage: "",
  licenseBackImage: "",
  address: "",
  notes: "",
};

const initialFiles = {
  profilePhoto: null,
  licenseFrontImage: null,
  licenseBackImage: null,
};

const ALLOWED_DRIVER_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];
const MAX_DRIVER_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;

const statusOptions = [
  { value: "available", label: "Available" },
  { value: "offline", label: "Offline" },
  { value: "inactive", label: "Inactive" },
];

const licenseFilterOptions = [
  { value: "all", label: "All License Statuses" },
  { value: "valid", label: "Valid" },
  { value: "expiring", label: "Expiring Soon" },
  { value: "expired", label: "Expired" },
];

function getUploadUrl(value) {
  if (!value) return "";
  if (String(value).startsWith("http") || String(value).startsWith("data:")) return value;
  return `${API_ORIGIN}${value}`;
}

function splitFullName(fullName = "") {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts.slice(0, -1).join(" ") || parts[0] || "",
    lastName: parts.length > 1 ? parts[parts.length - 1] : "",
  };
}

function formatDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function formatDateLabel(value) {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getStatusLabel(value) {
  if (value === "on_trip") return "On Trip";
  return statusOptions.find((option) => option.value === value)?.label || "Available";
}

function getStatusBadgeClass(value) {
  if (value === "available") return "badge badge-available";
  if (value === "on_trip") return "badge badge-pending";
  if (value === "offline") return "badge driver-badge-offline";
  if (value === "inactive") return "badge driver-badge-offline";
  return "badge";
}

function getLicenseStatus(driver) {
  if (!driver?.licenseExpiryDate) return "expired";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(driver.licenseExpiryDate);
  expiry.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (Number.isNaN(daysLeft) || daysLeft < 0) return "expired";
  if (daysLeft <= 30) return "expiring";
  return "valid";
}

function getLicenseBadgeClass(status) {
  if (status === "valid") return "badge badge-available";
  if (status === "expiring") return "badge badge-pending";
  return "badge badge-rejected";
}

function getLicenseLabel(status) {
  if (status === "valid") return "Valid";
  if (status === "expiring") return "Expiring Soon";
  return "Expired";
}

function validateDriverImageFile(file, label = "Driver image") {
  if (!file) return "";
  if (!ALLOWED_DRIVER_IMAGE_TYPES.includes(String(file.type || "").toLowerCase())) {
    return `${label} must be a JPG, PNG, or WEBP image.`;
  }
  if (file.size > MAX_DRIVER_IMAGE_SIZE_BYTES) {
    return `${label} must be 2MB or smaller.`;
  }
  return "";
}

function isBookingToday(booking) {
  if (!booking) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endToday = new Date(today);
  endToday.setHours(23, 59, 59, 999);
  return new Date(booking.startDate) <= endToday && new Date(booking.endDate) >= today;
}

function getDisplayDriverStatus(driver) {
  if (driver.status === "offline" || driver.status === "inactive") return driver.status;
  return isBookingToday(driver.currentAssignment) ? "on_trip" : "available";
}

function getActivityLabel(driver) {
  if (!driver.currentAssignment) return "No active assignment";
  return `${formatDateLabel(driver.currentAssignment.startDate)} - ${formatDateLabel(driver.currentAssignment.endDate)}`;
}

function getMonthDays(baseDate = new Date()) {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function dateKey(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function isDateWithinBooking(day, booking) {
  const dayTime = new Date(`${dateKey(day)}T12:00:00.000Z`).getTime();
  const startTime = new Date(booking.startDate).getTime();
  const endTime = new Date(booking.endDate).getTime();
  return dayTime >= startTime && dayTime <= endTime;
}

function getBookingsForDay(day, bookings = []) {
  return bookings.filter((booking) => isDateWithinBooking(day, booking));
}

function datesOverlap(startA, endA, startB, endB) {
  const aStart = new Date(startA).getTime();
  const aEnd = new Date(endA).getTime();
  const bStart = new Date(startB).getTime();
  const bEnd = new Date(endB).getTime();
  if ([aStart, aEnd, bStart, bEnd].some(Number.isNaN)) return false;
  return aStart <= bEnd && aEnd >= bStart;
}

function getBookingDateRange(booking) {
  return `${formatDateLabel(booking?.startDate)} - ${formatDateLabel(booking?.endDate)}`;
}

function buildPreview(file, existingValue) {
  if (file) return URL.createObjectURL(file);
  return getUploadUrl(existingValue);
}

function isFutureDate(value) {
  if (!value) return false;
  const selected = new Date(`${value}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return selected > today;
}

function getDriverValidationErrors(formData, files) {
  const nextErrors = {};
  const namePattern = /^[A-Za-z\s-]+$/;

  if (!formData.firstName.trim()) {
    nextErrors.firstName = "First name is required.";
  } else if (!namePattern.test(formData.firstName.trim())) {
    nextErrors.firstName = "First name can only use letters, spaces, and hyphen.";
  }

  if (!formData.lastName.trim()) {
    nextErrors.lastName = "Last name is required.";
  } else if (!namePattern.test(formData.lastName.trim())) {
    nextErrors.lastName = "Last name can only use letters, spaces, and hyphen.";
  }

  if (!/^09\d{9}$/.test(formData.contactNumber.trim())) {
    nextErrors.contactNumber = "Contact number must be exactly 11 digits and start with 09.";
  }

  if (!formData.licenseNumber.trim()) {
    nextErrors.licenseNumber = "License number is required.";
  } else if (formData.licenseNumber.trim().length < 5) {
    nextErrors.licenseNumber = "License number must be at least 5 characters.";
  }

  if (!formData.licenseExpiryDate) {
    nextErrors.licenseExpiryDate = "Expiry date is required.";
  } else if (!isFutureDate(formData.licenseExpiryDate)) {
    nextErrors.licenseExpiryDate = "License expiry date must be a future date.";
  }

  if (!formData.address.trim()) {
    nextErrors.address = "Address is required.";
  }

  if (!files.licenseFrontImage && !formData.licenseFrontImage) {
    nextErrors.licenseFrontImage = "Driver's License front image is required.";
  }

  if (!files.licenseBackImage && !formData.licenseBackImage) {
    nextErrors.licenseBackImage = "Driver's License back image is required.";
  }

  return nextErrors;
}

function UploadBox({ label, file, value, onChange, onRemove, error }) {
  const preview = buildPreview(file, value);
  const filename = file?.name || (value ? String(value).split("/").pop() : "");

  return (
    <label className={`driver-upload-box ${error ? "is-error" : ""}`}>
      <input
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        onChange={(event) => onChange(event.target.files?.[0] || null)}
      />
      {preview ? (
        <div className="driver-upload-preview">
          <img src={preview} alt={label} />
          <div>
            <strong>{filename}</strong>
            <button type="button" onClick={(event) => {
              event.preventDefault();
              onRemove();
            }}>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="driver-upload-empty">
          <span>Upload</span>
          <strong>{label}</strong>
          <em>Drag & Drop or Click to Upload</em>
        </div>
      )}
      {error ? <span className="field-error driver-upload-error">{error}</span> : null}
    </label>
  );
}

export default function Drivers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "technical_admin" || user?.role === "admin";
  const isStaff = user?.role === "staff";
  const canAccessPage = isAdmin || isStaff;

  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [licenseFilter, setLicenseFilter] = useState("all");
  const [summaryFilter, setSummaryFilter] = useState("all");
  const [archivedFilter, setArchivedFilter] = useState(false);
  const [msg, setMsg] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [files, setFiles] = useState(initialFiles);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profileTarget, setProfileTarget] = useState(null);
  const [actionsOpenId, setActionsOpenId] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(dateKey(new Date()));
  const [assignmentTarget, setAssignmentTarget] = useState(null);
  const [assignmentBookings, setAssignmentBookings] = useState([]);
  const [assignmentSchedule, setAssignmentSchedule] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [draggedBookingId, setDraggedBookingId] = useState("");
  const [driverDropActive, setDriverDropActive] = useState(false);
  const [assignmentConflict, setAssignmentConflict] = useState(null);
  const [assignmentConfirm, setAssignmentConfirm] = useState(null);
  const [assignmentSubmitting, setAssignmentSubmitting] = useState(false);
  const [archiveModal, setArchiveModal] = useState(null);
  const [deactivateModal, setDeactivateModal] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState("");

  const query = useMemo(
    () => ({
      search,
      status: statusFilter === "all" || archivedFilter ? "" : statusFilter,
      archived: archivedFilter ? "true" : "false",
    }),
    [search, statusFilter, archivedFilter]
  );

  const loadDrivers = async () => {
    setMsg("");
    try {
      const res = await api.get("/drivers", { params: query });
      setDrivers(res.data.drivers || []);
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to load drivers.");
    }
  };

  useEffect(() => {
    if (canAccessPage) loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.search, query.status, query.archived, canAccessPage]);

  const filteredDrivers = useMemo(() => {
    return drivers.filter((driver) => {
      const licenseStatus = getLicenseStatus(driver);
      const displayStatus = getDisplayDriverStatus(driver);
      const matchesLicense = licenseFilter === "all" || licenseStatus === licenseFilter;
      const matchesSummary =
        summaryFilter === "all" ||
        (summaryFilter === "available" && displayStatus === "available") ||
        (summaryFilter === "on_trip" && displayStatus === "on_trip") ||
        (summaryFilter === "expiring" && licenseStatus === "expiring");

      return matchesLicense && matchesSummary;
    });
  }, [drivers, licenseFilter, summaryFilter]);

  const kpis = useMemo(() => {
    const expiring = drivers.filter((driver) => getLicenseStatus(driver) === "expiring").length;
    return [
      {
        key: "all",
        label: "Total Drivers",
        value: drivers.length,
        helper: "All driver records",
        title: "Show all drivers",
      },
      {
        key: "available",
        label: "Available",
        value: drivers.filter((driver) => getDisplayDriverStatus(driver) === "available").length,
        helper: "Ready for assignment",
        title: "Show available drivers",
      },
      {
        key: "on_trip",
        label: "On Trip",
        value: drivers.filter((driver) => getDisplayDriverStatus(driver) === "on_trip").length,
        helper: "Currently assigned",
        title: "Show drivers on trip",
      },
      {
        key: "expiring",
        label: "Expiring Licenses",
        value: expiring,
        helper: "Due within 30 days",
        title: "Show drivers with expiring licenses",
      },
    ];
  }, [drivers]);

  const applySummaryFilter = (key) => {
    setSummaryFilter(key);

    if (key === "all") {
      setStatusFilter("all");
      setLicenseFilter("all");
      return;
    }

    if (key === "available") {
      setStatusFilter("available");
      setLicenseFilter("all");
      return;
    }

    if (key === "on_trip") {
      setStatusFilter("all");
      setLicenseFilter("all");
      return;
    }

    if (key === "expiring") {
      setStatusFilter("all");
      setLicenseFilter("expiring");
    }
  };

  const handleSummaryFilterKeyDown = (event, key) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    applySummaryFilter(key);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setSummaryFilter(value === "available" ? "available" : "all");
  };

  const handleLicenseFilterChange = (value) => {
    setLicenseFilter(value);
    setSummaryFilter(value === "expiring" ? "expiring" : "all");
  };

  const isSubmitDisabled = useMemo(() => {
    return Object.keys(getDriverValidationErrors(formData, files)).length > 0;
  }, [files, formData]);

  const updateFormField = (key, value) => {
    const next = { ...formData, [key]: value };
    setFormData(next);
    setErrors(getDriverValidationErrors(next, files));
  };

  const updateFileField = (key, file) => {
    const labels = {
      profilePhoto: "Driver profile image",
      licenseFrontImage: "Driver license front image",
      licenseBackImage: "Driver license back image",
    };
    const validationError = validateDriverImageFile(file, labels[key]);
    if (validationError) {
      setErrors((current) => ({ ...current, [key]: validationError }));
      return;
    }

    const next = { ...files, [key]: file };
    setFiles(next);
    setErrors(getDriverValidationErrors(formData, next));
  };

  const removeFileField = (key) => {
    const nextForm = { ...formData, [key]: "" };
    const nextFiles = { ...files, [key]: null };
    setFormData(nextForm);
    setFiles(nextFiles);
    setErrors(getDriverValidationErrors(nextForm, nextFiles));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setFiles(initialFiles);
    setErrors({});
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setMsg("");
    setShowModal(true);
  };

  const openEditModal = (driver) => {
    const names = splitFullName(driver.fullName);
    setErrors({});
    setMsg("");
    setEditingId(driver._id);
    setFormData({
      firstName: names.firstName,
      lastName: names.lastName,
      contactNumber: driver.contactNumber || "",
      profilePhoto: driver.profilePhoto || "",
      licenseNumber: driver.licenseNumber || "",
      licenseExpiryDate: formatDate(driver.licenseExpiryDate),
      licenseFrontImage: driver.licenseFrontImage || "",
      licenseBackImage: driver.licenseBackImage || "",
      address: driver.address || "",
      status: driver.status || "available",
      notes: driver.notes || "",
    });
    setFiles(initialFiles);
    setShowModal(true);
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setShowModal(false);
    resetForm();
  };

  const validateForm = () => {
    const nextErrors = getDriverValidationErrors(formData, files);
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitDriver = async (event) => {
    event.preventDefault();
    setMsg("");
    if (!validateForm()) {
      setMsg("Please fix the highlighted fields.");
      return;
    }

    const payload = new FormData();
    payload.append("fullName", `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim());
    payload.append("contactNumber", formData.contactNumber.trim());
    payload.append("licenseNumber", formData.licenseNumber.trim());
    payload.append("licenseExpiryDate", formData.licenseExpiryDate);
    payload.append("address", formData.address.trim());
    if (!editingId) payload.append("status", "available");
    payload.append("notes", formData.notes.trim());
    payload.append("profilePhoto", files.profilePhoto || formData.profilePhoto);
    payload.append("licenseFrontImage", files.licenseFrontImage || formData.licenseFrontImage);
    payload.append("licenseBackImage", files.licenseBackImage || formData.licenseBackImage);

    try {
      setIsSubmitting(true);
      if (editingId) {
        await api.patch(`/drivers/${editingId}`, payload);
        setMsg("Driver updated successfully.");
      } else {
        await api.post("/drivers", payload);
        setMsg("Driver added successfully");
      }
      setShowModal(false);
      resetForm();
      await loadDrivers();
    } catch (err) {
      if (err?.response?.data?.errors) setErrors(err.response.data.errors);
      setMsg(err?.response?.data?.message || "Failed to save driver.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const setDriverStatus = async (driver, status) => {
    if (actionLoadingId) return;
    try {
      setMsg("");
      setActionLoadingId(`status:${driver._id}`);
      await api.patch(`/drivers/${driver._id}`, { status });
      await loadDrivers();
      setMsg("Driver status updated.");
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to update driver status.");
    } finally {
      setActionLoadingId("");
    }
  };

  const openDeactivateModal = (driver) => {
    if (!isAdmin) return;
    setActionsOpenId(null);
    setDeactivateModal({ driver, reason: "", error: "", submitting: false });
  };

  const confirmDeactivate = async () => {
    if (!deactivateModal?.driver?._id || deactivateModal.submitting) return;
    const reason = String(deactivateModal.reason || "").trim();
    setDeactivateModal((prev) => ({ ...prev, error: "", submitting: true }));
    try {
      await api.patch(`/drivers/${deactivateModal.driver._id}/deactivate`, { reason });
      setDeactivateModal(null);
      await loadDrivers();
      setMsg("Driver deactivated successfully.");
    } catch (err) {
      setDeactivateModal((prev) => ({
        ...prev,
        submitting: false,
        error: err?.response?.data?.message || "Failed to deactivate driver.",
      }));
    }
  };

  const activateDriver = async (driver) => {
    if (!isAdmin || actionLoadingId) return;
    setActionsOpenId(null);
    setMsg("");
    setActionLoadingId(`activate:${driver._id}`);
    try {
      await api.patch(`/drivers/${driver._id}/activate`);
      await loadDrivers();
      setMsg("Driver reactivated successfully.");
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to reactivate driver.");
    } finally {
      setActionLoadingId("");
    }
  };

  const openArchiveModal = (driver) => {
    if (!isAdmin) return;
    setActionsOpenId(null);
    setArchiveModal({ driver, reason: "", error: "", submitting: false });
  };

  const confirmArchive = async () => {
    if (!archiveModal?.driver?._id || archiveModal.submitting) return;
    const reason = String(archiveModal.reason || "").trim();
    if (!reason) {
      setArchiveModal((prev) => ({ ...prev, error: "Please enter a reason for archiving." }));
      return;
    }
    setArchiveModal((prev) => ({ ...prev, error: "", submitting: true }));
    try {
      await api.patch(`/drivers/${archiveModal.driver._id}/archive`, { reason });
      setArchiveModal(null);
      await loadDrivers();
      setMsg("Driver archived successfully.");
    } catch (err) {
      setArchiveModal((prev) => ({
        ...prev,
        submitting: false,
        error: err?.response?.data?.message || "Failed to archive driver.",
      }));
    }
  };

  const restoreDriver = async (driver) => {
    if (!isAdmin || actionLoadingId) return;
    setActionsOpenId(null);
    setMsg("");
    setActionLoadingId(`restore:${driver._id}`);
    try {
      await api.patch(`/drivers/${driver._id}/restore`);
      await loadDrivers();
      setMsg("Driver restored successfully.");
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to restore driver.");
    } finally {
      setActionLoadingId("");
    }
  };

  const openSchedule = async (driver) => {
    setScheduleTarget(driver);
    setScheduleData(null);
    setScheduleLoading(true);
    setCalendarMonth(new Date());
    setSelectedDate(dateKey(new Date()));
    setMsg("");
    try {
      const res = await api.get(`/drivers/${driver._id}/schedule`, { params: { days: 180 } });
      setScheduleData(res.data);
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to load driver schedule.");
      setScheduleTarget(null);
    } finally {
      setScheduleLoading(false);
    }
  };

  const openDriverAssignment = async (driver) => {
    setActionsOpenId(null);
    setAssignmentTarget(driver);
    setAssignmentBookings([]);
    setAssignmentSchedule([]);
    setAssignmentLoading(true);
    setMsg("");
    try {
      const [bookingsRes, scheduleRes] = await Promise.all([
        api.get("/bookings"),
        api.get(`/drivers/${driver._id}/schedule`, { params: { days: 240 } }),
      ]);
      const terminalStatuses = ["draft", "rejected", "cancelled", "cancelled_conflict", "expired", "completed"];
      const openWithDriverBookings = (bookingsRes.data.bookings || []).filter((booking) => {
        const status = String(booking.status || "").toLowerCase();
        return booking.withDriver && !booking.driverId && !terminalStatuses.includes(status);
      });
      setAssignmentBookings(openWithDriverBookings);
      setAssignmentSchedule(scheduleRes.data.bookings || []);
    } catch (err) {
      setMsg(err?.response?.data?.message || "Failed to load driver assignment workspace.");
      setAssignmentTarget(null);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const closeDriverAssignment = () => {
    if (assignmentSubmitting) return;
    setAssignmentTarget(null);
    setAssignmentBookings([]);
    setAssignmentSchedule([]);
    setDraggedBookingId("");
    setDriverDropActive(false);
  };

  const validateBookingCandidate = (booking) => {
    if (!assignmentTarget) return "Select a driver first.";
    if (!booking) return "Booking was not found.";
    if (assignmentTarget.status === "offline" || assignmentTarget.status === "inactive") {
      return "This driver is offline or inactive and cannot be assigned.";
    }
    if (getLicenseStatus(assignmentTarget) === "expired") {
      return "This driver's license is expired or missing.";
    }
    const conflict = assignmentSchedule.find((assignedBooking) =>
      datesOverlap(booking.startDate, booking.endDate, assignedBooking.startDate, assignedBooking.endDate)
    );
    if (conflict) {
      return `Schedule conflict with ${conflict.bookingReference || "an assigned booking"} (${getBookingDateRange(conflict)}).`;
    }
    return "";
  };

  const requestBookingAssignment = (booking) => {
    const issue = validateBookingCandidate(booking);
    if (issue) {
      setAssignmentConflict({ booking, message: issue });
      return;
    }
    setAssignmentConfirm({ booking });
  };

  const confirmBookingAssignment = async () => {
    if (!assignmentTarget || !assignmentConfirm?.booking) return;
    setAssignmentSubmitting(true);
    setMsg("");
    try {
      await api.patch(`/bookings/${assignmentConfirm.booking._id}/assign-driver`, {
        driverId: assignmentTarget._id,
      });
      setMsg("Driver assigned to booking successfully.");
      setAssignmentConfirm(null);
      await loadDrivers();
      await openDriverAssignment(assignmentTarget);
    } catch (err) {
      setAssignmentConflict({
        booking: assignmentConfirm.booking,
        message: err?.response?.data?.message || "Failed to assign booking to this driver.",
      });
      setAssignmentConfirm(null);
    } finally {
      setAssignmentSubmitting(false);
    }
  };

  const calendarDays = useMemo(() => getMonthDays(calendarMonth), [calendarMonth]);
  const selectedBookings = useMemo(() => {
    const day = selectedDate ? new Date(`${selectedDate}T12:00:00`) : new Date();
    return getBookingsForDay(day, scheduleData?.bookings || []);
  }, [scheduleData, selectedDate]);

  if (!canAccessPage) return <Navigate to="/unauthorized" replace />;

  const driverLoadingTitle = actionLoadingId.startsWith("activate:")
    ? "Reactivating driver..."
    : actionLoadingId.startsWith("restore:")
    ? "Restoring driver..."
    : actionLoadingId.startsWith("status:")
    ? "Updating driver status..."
    : "Processing...";

  return (
    <AdminShell
      pageClassName="admin-shell--drivers"
      eyebrow="FLEET"
      title="Driver Management"
      subtitle="Manage driver profiles, assignments, availability, and operating status."
      actions={<button type="button" className="btn btn-primary" onClick={openAddModal}>Add Driver</button>}
    >
      <AdminLoadingOverlay
        open={Boolean(actionLoadingId)}
        title={driverLoadingTitle}
        message="Please wait while FleetX updates the driver record."
      />
      {msg ? <div className="dashboard-alert">{msg}</div> : null}

      <div className="driver-kpi-grid">
        {kpis.map((item) => (
          <article
            className={`driver-kpi-card ${summaryFilter === item.key ? "is-active" : ""}`}
            key={item.key}
            role="button"
            tabIndex={0}
            title={item.title}
            aria-label={item.title}
            aria-pressed={summaryFilter === item.key}
            onClick={() => applySummaryFilter(item.key)}
            onKeyDown={(event) => handleSummaryFilterKeyDown(event, item.key)}
          >
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <em>{item.helper}</em>
          </article>
        ))}
      </div>

      <div className="driver-toolbar driver-toolbar--premium">
        <input
          className="input"
          placeholder="Search driver name, contact number, or license number"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {!archivedFilter ? (
          <select className="select" value={statusFilter} onChange={(event) => handleStatusFilterChange(event.target.value)}>
            <option value="all">All Statuses</option>
            {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        ) : null}
        {!archivedFilter ? (
          <select className="select" value={licenseFilter} onChange={(event) => handleLicenseFilterChange(event.target.value)}>
            {licenseFilterOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        ) : null}
        <button
          type="button"
          className={`btn ${archivedFilter ? "btn-primary" : "btn-soft"}`}
          onClick={() => {
            setArchivedFilter((v) => !v);
            setStatusFilter("all");
            setLicenseFilter("all");
            setSummaryFilter("all");
          }}
        >
          {archivedFilter ? "Archived Drivers" : "View Archived"}
        </button>
      </div>

      <div className="vehicle-exact-panel driver-premium-panel">
        <div className="dashboard-table-wrap">
          <table className="driver-table driver-premium-table">
            <thead>
              <tr>
                <th>Driver</th>
                <th>License</th>
                <th>Status</th>
                <th>Assignment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrivers.map((driver) => {
                const licenseStatus = getLicenseStatus(driver);
                const displayStatus = getDisplayDriverStatus(driver);
                return (
                  <tr key={driver._id} onClick={() => setProfileTarget(driver)}>
                    <td>
                      <div className="driver-cell">
                        {driver.profilePhoto ? (
                          <img className="driver-avatar driver-avatar--sm" src={getUploadUrl(driver.profilePhoto)} alt={driver.fullName} />
                        ) : (
                          <div className="driver-avatar driver-avatar--empty driver-avatar--sm">{(driver.fullName || "D").charAt(0).toUpperCase()}</div>
                        )}
                        <div>
                          <div className="vehicle-exact-name">
                            {driver.fullName}
                            {driver.isArchived ? (
                              <span className="badge badge-muted" style={{ marginLeft: 6, fontSize: "0.7em" }}>Archived</span>
                            ) : null}
                          </div>
                          <div className="vehicle-exact-meta">Added {formatDateLabel(driver.createdAt)}</div>
                          <div className="vehicle-exact-meta">{driver.contactNumber || "No contact"}</div>
                          {driver.isArchived && driver.archiveReason ? (
                            <div className="vehicle-exact-meta">Reason: {driver.archiveReason}</div>
                          ) : null}
                          {driver.isArchived && driver.archivedAt ? (
                            <div className="vehicle-exact-meta">Archived {formatDateLabel(driver.archivedAt)}</div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="driver-license-stack">
                        <strong>{driver.licenseNumber || "N/A"}</strong>
                        <span>Expires {formatDateLabel(driver.licenseExpiryDate)}</span>
                        <span className={getLicenseBadgeClass(licenseStatus)}>{getLicenseLabel(licenseStatus)}</span>
                      </div>
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(displayStatus)}>{getStatusLabel(displayStatus)}</span>
                    </td>
                    <td>
                      {driver.currentAssignment ? (
                        <div className="driver-assignment-summary">
                          <strong>{driver.currentAssignment.bookingReference || "Booking"}</strong>
                          <span>{getActivityLabel(driver)}</span>
                        </div>
                      ) : (
                        <span className="vehicle-exact-meta">No assignment</span>
                      )}
                    </td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className="driver-action-row">
                        {!driver.isArchived ? (
                          <button className="driver-action-btn driver-action-btn--secondary" type="button" onClick={() => setProfileTarget(driver)}>View</button>
                        ) : (
                          <button className="driver-action-btn driver-action-btn--secondary" type="button" onClick={() => setProfileTarget(driver)}>View</button>
                        )}
                        {!driver.isArchived ? (
                          <button className="driver-action-btn driver-action-btn--primary" type="button" onClick={() => openSchedule(driver)}>Calendar</button>
                        ) : null}
                        <div className="action-menu-wrap">
                          <button className="driver-action-btn driver-action-btn--menu" type="button" onClick={() => setActionsOpenId(actionsOpenId === driver._id ? null : driver._id)}>More</button>
                          {actionsOpenId === driver._id ? (
                            <div className="action-menu-dropdown">
                              {!driver.isArchived ? (
                                <>
                                  <button className="action-menu-item" type="button" onClick={() => { setActionsOpenId(null); openEditModal(driver); }}>Edit</button>
                                  <button className="action-menu-item" type="button" onClick={() => openDriverAssignment(driver)}>Assign to Booking</button>
                                  {displayStatus === "on_trip" ? (
                                    <button className="action-menu-item warning" type="button" disabled>Set Offline — unavailable on trip</button>
                                  ) : driver.status === "offline" ? (
                                    <button className="action-menu-item success" type="button" onClick={() => { setActionsOpenId(null); setDriverStatus(driver, "available"); }}>Set Available</button>
                                  ) : (
                                    <button className="action-menu-item warning" type="button" onClick={() => { setActionsOpenId(null); setDriverStatus(driver, "offline"); }}>Set Offline</button>
                                  )}
                                  {isAdmin && driver.status !== "inactive" ? (
                                    <button className="action-menu-item warning" type="button" onClick={() => openDeactivateModal(driver)}>Deactivate Driver</button>
                                  ) : null}
                                  {isAdmin && driver.status === "inactive" ? (
                                    <button className="action-menu-item success" type="button" onClick={() => activateDriver(driver)}>Reactivate Driver</button>
                                  ) : null}
                                  {isAdmin ? (
                                    <button className="action-menu-item danger" type="button" onClick={() => openArchiveModal(driver)}>Archive Driver</button>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  {isAdmin ? (
                                    <button className="action-menu-item success" type="button" onClick={() => restoreDriver(driver)}>Restore Driver</button>
                                  ) : null}
                                </>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredDrivers.length === 0 ? (
                <tr><td colSpan="5" className="dashboard-empty-cell">No drivers found.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {showModal ? (
        <div className="modal-overlay vehicle-modal-overlay driver-modal-overlay" onClick={closeModal}>
          <div className="modal-box driver-modal-box driver-modal-box--composer" onClick={(event) => event.stopPropagation()}>
            <header className="driver-modal-header">
              <div className="driver-modal-heading">
                <span className="driver-modal-heading__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="3.25" /><path d="M5.5 19c.45-3.65 2.6-5.5 6.5-5.5s6.05 1.85 6.5 5.5" /></svg>
                </span>
                <div>
                  <div className="driver-modal-eyebrow">{editingId ? "UPDATE DRIVER" : "NEW DRIVER"}</div>
                  <h3>{editingId ? "Edit Driver" : "Add Driver"}</h3>
                  <p>{editingId ? "Update the driver profile and license details." : "Create a driver profile and upload license details."}</p>
                </div>
              </div>
              <button type="button" className="driver-modal-close" onClick={closeModal} aria-label={editingId ? "Close Edit Driver" : "Close Add Driver"}>×</button>
            </header>

            <form onSubmit={submitDriver} className="driver-modal-form">
              <div className="driver-modal-body">
                <div className="driver-modal-main">
                  <section className="driver-form-section">
                    <div className="driver-section-heading"><span>1</span><div><h4>Basic Information</h4><p>Enter the driver's personal and contact information.</p></div></div>
                    <div className="driver-form-grid">
                      <label className="field-group">
                        <span className="field-label">First Name <b>*</b></span>
                        <input className={`input ${errors.firstName ? "input-error" : ""}`} placeholder="Enter first name" value={formData.firstName} onChange={(event) => updateFormField("firstName", event.target.value)} />
                        {errors.firstName ? <span className="field-error">{errors.firstName}</span> : null}
                      </label>
                      <label className="field-group">
                        <span className="field-label">Last Name <b>*</b></span>
                        <input className={`input ${errors.lastName ? "input-error" : ""}`} placeholder="Enter last name" value={formData.lastName} onChange={(event) => updateFormField("lastName", event.target.value)} />
                        {errors.lastName ? <span className="field-error">{errors.lastName}</span> : null}
                      </label>
                      <label className="field-group driver-field-wide">
                        <span className="field-label">Contact Number <b>*</b></span>
                        <input className={`input ${errors.contactNumber ? "input-error" : ""}`} placeholder="e.g. 09XXXXXXXXX" value={formData.contactNumber} onChange={(event) => updateFormField("contactNumber", event.target.value.replace(/\D/g, "").slice(0, 11))} />
                        {errors.contactNumber ? <span className="field-error">{errors.contactNumber}</span> : null}
                      </label>
                    </div>
                  </section>

                  <section className="driver-form-section">
                    <div className="driver-section-heading"><span>2</span><div><h4>License Information</h4><p>Enter license details and expiry date.</p></div></div>
                    <div className="driver-form-grid">
                      <label className="field-group">
                        <span className="field-label">License Number <b>*</b></span>
                        <input className={`input ${errors.licenseNumber ? "input-error" : ""}`} placeholder="Enter license number" value={formData.licenseNumber} onChange={(event) => updateFormField("licenseNumber", event.target.value)} />
                        {errors.licenseNumber ? <span className="field-error">{errors.licenseNumber}</span> : null}
                      </label>
                      <label className="field-group">
                        <span className="field-label">Expiry Date <b>*</b></span>
                        <input className={`input ${errors.licenseExpiryDate ? "input-error" : ""}`} type="date" value={formData.licenseExpiryDate} onChange={(event) => updateFormField("licenseExpiryDate", event.target.value)} />
                        {errors.licenseExpiryDate ? <span className="field-error">{errors.licenseExpiryDate}</span> : null}
                      </label>
                    </div>
                  </section>

                  <section className="driver-form-section">
                    <div className="driver-section-heading"><span>3</span><div><h4>Additional Driver Details</h4><p>Add the driver's address and any useful notes.</p></div></div>
                    <div className="driver-form-grid">
                      <label className="field-group driver-field-wide">
                        <span className="field-label">Address <b>*</b></span>
                        <input className={`input ${errors.address ? "input-error" : ""}`} placeholder="Enter complete address" value={formData.address} onChange={(event) => updateFormField("address", event.target.value)} />
                        {errors.address ? <span className="field-error">{errors.address}</span> : null}
                      </label>
                      <label className="field-group driver-field-wide">
                        <span className="field-label">Notes / Experience</span>
                        <textarea className="input" placeholder="Add any additional notes (optional)" value={formData.notes} onChange={(event) => updateFormField("notes", event.target.value)} />
                      </label>
                    </div>
                  </section>
                </div>

                <aside className="driver-modal-aside">
                  <section className="driver-side-panel driver-profile-upload-section">
                    <div className="driver-side-panel__heading"><span aria-hidden="true">◎</span><div><h4>Profile Photo</h4><p>Add a clear photo of the driver.</p></div></div>
                    <div className="driver-profile-upload">
                      <div className="driver-profile-preview">
                        {buildPreview(files.profilePhoto, formData.profilePhoto) ? (
                          <img src={buildPreview(files.profilePhoto, formData.profilePhoto)} alt="Driver preview" />
                        ) : (
                          <span>{formData.firstName ? formData.firstName.charAt(0).toUpperCase() : "D"}</span>
                        )}
                      </div>
                      <label className="btn btn-soft driver-profile-upload__button">
                        Upload Photo
                        <input hidden type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(event) => updateFileField("profilePhoto", event.target.files?.[0] || null)} />
                      </label>
                      <small>JPG, PNG or WEBP up to 2 MB</small>
                    </div>
                  </section>

                  <section className="driver-side-panel">
                    <div className="driver-side-panel__heading"><span aria-hidden="true">▤</span><div><h4>License Documents</h4><p>Upload clear images of the driver's license.</p></div></div>
                    <div className="driver-modal-document-grid">
                      <UploadBox label="Driver's License Front" file={files.licenseFrontImage} value={formData.licenseFrontImage} error={errors.licenseFrontImage} onChange={(file) => updateFileField("licenseFrontImage", file)} onRemove={() => removeFileField("licenseFrontImage")} />
                      <UploadBox label="Driver's License Back" file={files.licenseBackImage} value={formData.licenseBackImage} error={errors.licenseBackImage} onChange={(file) => updateFileField("licenseBackImage", file)} onRemove={() => removeFileField("licenseBackImage")} />
                    </div>
                    <small className="driver-upload-guidance">JPG, PNG or WEBP up to 2 MB each</small>
                  </section>
                </aside>
              </div>

              <footer className="driver-modal-actions">
                <button type="button" className="btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary driver-submit-btn" disabled={isSubmitting || isSubmitDisabled}>
                  {isSubmitting ? "Saving..." : editingId ? "Save Changes" : "Add Driver"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      {profileTarget ? (
        <div className="modal-overlay driver-profile-modal-overlay" onClick={() => setProfileTarget(null)}>
          <div className="modal-box driver-profile-modal" onClick={(event) => event.stopPropagation()}>
            <div className="driver-profile-modal__header">
              <div className="driver-profile-heading">
                {profileTarget.profilePhoto ? <img src={getUploadUrl(profileTarget.profilePhoto)} alt={profileTarget.fullName} /> : <div>{(profileTarget.fullName || "D").charAt(0).toUpperCase()}</div>}
                <div className="driver-profile-heading__copy">
                  <h3>{profileTarget.fullName}</h3>
                  <span className={getStatusBadgeClass(getDisplayDriverStatus(profileTarget))}>{getStatusLabel(getDisplayDriverStatus(profileTarget))}</span>
                </div>
              </div>
              <button type="button" className="driver-profile-modal__close" onClick={() => setProfileTarget(null)} aria-label="Close">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            <div className="driver-profile-details">
              <div><span>Contact</span><strong>{profileTarget.contactNumber || "N/A"}</strong></div>
              <div><span>License</span><strong>{profileTarget.licenseNumber || "N/A"}</strong></div>
              <div><span>Expiry</span><strong>{formatDateLabel(profileTarget.licenseExpiryDate)}</strong></div>
              <div><span>Address</span><strong>{profileTarget.address || "N/A"}</strong></div>
              <div className="driver-profile-details__wide"><span>Notes / Experience</span><strong>{profileTarget.notes || "N/A"}</strong></div>
            </div>
          </div>
        </div>
      ) : null}

      {assignmentTarget ? (
        <div className="modal-overlay" onClick={closeDriverAssignment}>
          <div className="modal-box driver-booking-assignment-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div className="driver-profile-heading">
                {assignmentTarget.profilePhoto ? (
                  <img src={getUploadUrl(assignmentTarget.profilePhoto)} alt={assignmentTarget.fullName} />
                ) : (
                  <div>{(assignmentTarget.fullName || "D").charAt(0).toUpperCase()}</div>
                )}
                <div>
                  <div className="vehicle-modal-eyebrow">DRIVER ASSIGNMENT</div>
                  <h3 className="admin-section-title" style={{ margin: 0 }}>{assignmentTarget.fullName}</h3>
                  <span className={getStatusBadgeClass(getDisplayDriverStatus(assignmentTarget))}>
                    {getStatusLabel(getDisplayDriverStatus(assignmentTarget))}
                  </span>
                </div>
              </div>
              <button type="button" className="btn-close" onClick={closeDriverAssignment}>Close</button>
            </div>

            {assignmentLoading ? (
              <div className="dashboard-empty-cell">Loading assignment workspace...</div>
            ) : (
              <div className="driver-reverse-assignment">
                <section className="driver-reverse-bookings">
                  <div className="driver-reverse-head">
                    <div>
                      <strong>With-driver bookings</strong>
                      <span>{assignmentBookings.length} unassigned booking(s)</span>
                    </div>
                  </div>
                  <div className="driver-reverse-booking-list">
                    {assignmentBookings.map((booking) => {
                      const issue = validateBookingCandidate(booking);
                      const valid = !issue;
                      return (
                        <article
                          key={booking._id}
                          className={`driver-reverse-booking-card ${valid ? "is-valid" : "is-invalid"}`}
                          draggable={valid}
                          onDragStart={(event) => {
                            event.dataTransfer.setData("text/booking-id", booking._id);
                            event.dataTransfer.effectAllowed = "move";
                            setDraggedBookingId(booking._id);
                          }}
                          onDragEnd={() => {
                            setDraggedBookingId("");
                            setDriverDropActive(false);
                          }}
                          onClick={() => requestBookingAssignment(booking)}
                        >
                          <div>
                            <strong>{booking.bookingReference || "Booking"}</strong>
                            <span>{booking.fullName || "Customer"}</span>
                            <small>{booking.vehicleId ? `${booking.vehicleId.make || ""} ${booking.vehicleId.model || ""}`.trim() : "Vehicle"}</small>
                          </div>
                          <div>
                            <span>{getBookingDateRange(booking)}</span>
                            <small>{valid ? "Ready to assign" : issue}</small>
                          </div>
                        </article>
                      );
                    })}
                    {assignmentBookings.length === 0 ? (
                      <div className="dashboard-empty-cell">No unassigned with-driver bookings.</div>
                    ) : null}
                  </div>
                </section>

                <section className="driver-reverse-drop-panel">
                  <div className="driver-reverse-driver-card">
                    <span>Assign to</span>
                    <strong>{assignmentTarget.fullName}</strong>
                    <small>
                      License {getLicenseLabel(getLicenseStatus(assignmentTarget))} · {assignmentSchedule.length} scheduled booking(s)
                    </small>
                  </div>

                  <div
                    className={`driver-reverse-drop-zone ${driverDropActive ? "is-active" : ""}`}
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                      setDriverDropActive(true);
                    }}
                    onDragLeave={() => setDriverDropActive(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      const droppedId = event.dataTransfer.getData("text/booking-id") || draggedBookingId;
                      const booking = assignmentBookings.find((item) => String(item._id) === String(droppedId));
                      setDriverDropActive(false);
                      setDraggedBookingId("");
                      requestBookingAssignment(booking);
                    }}
                  >
                    <strong>Drop booking here</strong>
                    <span>Schedule, driver status, and license validity will be checked first.</span>
                  </div>

                  <div className="driver-reverse-calendar">
                    <strong>Schedule preview</strong>
                    <div>
                      {assignmentSchedule.slice(0, 4).map((booking) => (
                        <span key={booking._id}>
                          {booking.bookingReference || "Booking"} · {getBookingDateRange(booking)}
                        </span>
                      ))}
                      {assignmentSchedule.length === 0 ? <span>No scheduled bookings.</span> : null}
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {assignmentConflict ? (
        <div className="modal-overlay assignment-feedback-overlay" onClick={() => setAssignmentConflict(null)}>
          <div className="assignment-feedback-modal assignment-feedback-modal--conflict" onClick={(event) => event.stopPropagation()}>
            <span className="assignment-feedback-kicker">Assignment blocked</span>
            <h3>Cannot Assign Booking</h3>
            <p>{assignmentConflict.message}</p>
            {assignmentConflict.booking ? (
              <div className="assignment-feedback-driver">
                <strong>{assignmentConflict.booking.bookingReference || "Booking"}</strong>
                <span>{getBookingDateRange(assignmentConflict.booking)}</span>
              </div>
            ) : null}
            <div className="assignment-feedback-actions">
              <button type="button" className="btn btn-soft" onClick={() => setAssignmentConflict(null)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}

      {assignmentConfirm ? (
        <div className="modal-overlay assignment-feedback-overlay" onClick={() => setAssignmentConfirm(null)}>
          <div className="assignment-feedback-modal" onClick={(event) => event.stopPropagation()}>
            <span className="assignment-feedback-kicker">Ready to assign</span>
            <h3>Confirm Booking Assignment</h3>
            <p>
              Assign <strong>{assignmentTarget?.fullName}</strong> to{" "}
              <strong>{assignmentConfirm.booking.bookingReference || "this booking"}</strong> for{" "}
              {getBookingDateRange(assignmentConfirm.booking)}.
            </p>
            <div className="assignment-feedback-driver">
              <strong>{assignmentConfirm.booking.fullName || "Customer"}</strong>
              <span>{assignmentConfirm.booking.vehicleId ? `${assignmentConfirm.booking.vehicleId.make || ""} ${assignmentConfirm.booking.vehicleId.model || ""}`.trim() : "Vehicle"}</span>
            </div>
            <div className="assignment-feedback-actions">
              <button type="button" className="btn btn-soft" onClick={() => setAssignmentConfirm(null)} disabled={assignmentSubmitting}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={confirmBookingAssignment} disabled={assignmentSubmitting}>
                {assignmentSubmitting ? "Assigning..." : "Confirm Assignment"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deactivateModal ? (
        <div className="modal-overlay" onClick={() => !deactivateModal.submitting && setDeactivateModal(null)}>
          <div
            className="modal-box modal-box--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="driver-deactivate-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3 id="driver-deactivate-title" className="admin-section-title" style={{ margin: 0 }}>
                  Deactivate this driver?
                </h3>
                <span className="muted">{deactivateModal.driver?.fullName}</span>
              </div>
              <button type="button" className="btn-close" onClick={() => setDeactivateModal(null)} disabled={deactivateModal.submitting}>Close</button>
            </div>
            <p className="muted" style={{ marginBottom: 8 }}>
              This driver will no longer be available for assignment, but their booking history will be preserved. They can be reactivated later.
            </p>
            <div className="field-group" style={{ marginBottom: 8 }}>
              <label className="vehicle-field-label">
                <span>Reason (optional)</span>
              </label>
              <select
                className="select"
                value={
                  ["License expired", "Temporarily unavailable", "On leave", "Other"].includes(deactivateModal.reason)
                    ? deactivateModal.reason
                    : deactivateModal.reason ? "Other" : ""
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (val !== "Other" && val !== "") {
                    setDeactivateModal((prev) => ({ ...prev, reason: val }));
                  } else if (val === "Other") {
                    setDeactivateModal((prev) => ({ ...prev, reason: "" }));
                  }
                }}
                disabled={deactivateModal.submitting}
              >
                <option value="">Select a reason (optional)...</option>
                <option>License expired</option>
                <option>Temporarily unavailable</option>
                <option>On leave</option>
                <option>Other</option>
              </select>
            </div>
            <div className="field-group" style={{ marginBottom: 4 }}>
              <textarea
                className="input"
                rows={2}
                placeholder="Add a reason or note (optional)..."
                value={deactivateModal.reason}
                disabled={deactivateModal.submitting}
                onChange={(e) => setDeactivateModal((prev) => ({ ...prev, reason: e.target.value, error: "" }))}
              />
            </div>
            {deactivateModal.error ? (
              <p className="field-error" style={{ marginBottom: 8 }}>{deactivateModal.error}</p>
            ) : null}
            <div className="btn-row" style={{ marginTop: 16 }}>
              <button type="button" className="btn" onClick={() => setDeactivateModal(null)} disabled={deactivateModal.submitting}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={confirmDeactivate} disabled={deactivateModal.submitting}>
                {deactivateModal.submitting ? "Deactivating..." : "Deactivate Driver"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {archiveModal ? (
        <div className="modal-overlay" onClick={() => !archiveModal.submitting && setArchiveModal(null)}>
          <div
            className="modal-box modal-box--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="driver-archive-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3 id="driver-archive-title" className="admin-section-title" style={{ margin: 0 }}>
                  Archive this driver?
                </h3>
                <span className="muted">{archiveModal.driver?.fullName}</span>
              </div>
              <button type="button" className="btn-close" onClick={() => setArchiveModal(null)} disabled={archiveModal.submitting}>Close</button>
            </div>
            <p className="muted" style={{ marginBottom: 8 }}>
              This driver will be hidden from active driver lists but kept for booking history and records. They can be restored later.
            </p>
            <div className="field-group" style={{ marginBottom: 8 }}>
              <label className="vehicle-field-label">
                <span>Reason for archiving</span>
                <b>*</b>
              </label>
              <select
                className="select"
                value={
                  ["Driver no longer active", "Resigned / no longer affiliated", "Duplicate test driver", "Administrative cleanup", "Other"].includes(archiveModal.reason)
                    ? archiveModal.reason
                    : archiveModal.reason ? "Other" : ""
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (val !== "Other" && val !== "") {
                    setArchiveModal((prev) => ({ ...prev, reason: val, error: "" }));
                  } else if (val === "Other") {
                    setArchiveModal((prev) => ({ ...prev, reason: "", error: "" }));
                  }
                }}
                disabled={archiveModal.submitting}
              >
                <option value="">Select a reason...</option>
                <option>Driver no longer active</option>
                <option>Resigned / no longer affiliated</option>
                <option>Duplicate test driver</option>
                <option>Administrative cleanup</option>
                <option>Other</option>
              </select>
            </div>
            <div className="field-group" style={{ marginBottom: 4 }}>
              <textarea
                className="input"
                rows={3}
                placeholder="Enter reason for archiving this driver..."
                value={archiveModal.reason}
                disabled={archiveModal.submitting}
                onChange={(e) => setArchiveModal((prev) => ({ ...prev, reason: e.target.value, error: "" }))}
              />
            </div>
            {archiveModal.error ? (
              <p className="field-error" style={{ marginBottom: 8 }}>{archiveModal.error}</p>
            ) : null}
            <div className="btn-row" style={{ marginTop: 16 }}>
              <button type="button" className="btn" onClick={() => setArchiveModal(null)} disabled={archiveModal.submitting}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={confirmArchive} disabled={archiveModal.submitting}>
                {archiveModal.submitting ? "Archiving..." : "Archive Driver"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {scheduleTarget ? (
        <div className="modal-overlay" onClick={() => setScheduleTarget(null)}>
          <div className="modal-box driver-calendar-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="vehicle-modal-eyebrow">DRIVER CALENDAR</div>
                <h3 className="admin-section-title" style={{ margin: 0 }}>{scheduleTarget.fullName}</h3>
                <span className={getStatusBadgeClass(getDisplayDriverStatus(scheduleTarget))}>{getStatusLabel(getDisplayDriverStatus(scheduleTarget))}</span>
              </div>
              <button type="button" className="btn-close" onClick={() => setScheduleTarget(null)}>Close</button>
            </div>

            {scheduleLoading ? (
              <div className="dashboard-empty-cell">Loading schedule...</div>
            ) : (
              <div className="driver-calendar-layout">
                <section>
                  <div className="driver-calendar-head">
                    <button className="btn btn-soft" type="button" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}>Prev</button>
                    <strong>{calendarMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</strong>
                    <button className="btn btn-soft" type="button" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}>Next</button>
                  </div>
                  <div className="driver-month-grid">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <span key={day}>{day}</span>)}
                    {calendarDays.map((day) => {
                      const bookings = getBookingsForDay(day, scheduleData?.bookings || []);
                      const key = dateKey(day);
                      return (
                        <button
                          type="button"
                          key={key}
                          className={`driver-month-day ${bookings.length ? "is-booked" : ""} ${key === selectedDate ? "is-selected" : ""} ${day.getMonth() !== calendarMonth.getMonth() ? "is-muted" : ""}`}
                          onClick={() => setSelectedDate(key)}
                        >
                          <strong>{day.getDate()}</strong>
                          {bookings.length ? <em>{bookings.length} booking</em> : null}
                        </button>
                      );
                    })}
                  </div>
                </section>
                <aside className="driver-calendar-bookings">
                  <h4>Bookings on {formatDateLabel(selectedDate)}</h4>
                  {selectedBookings.length ? selectedBookings.map((booking) => (
                    <article className="driver-booking-card" key={booking._id}>
                      <strong>{booking.bookingReference || "Booking"}</strong>
                      <span>{booking.fullName || "Customer"}</span>
                      <span>{booking.vehicleId ? `${booking.vehicleId.make || ""} ${booking.vehicleId.model || ""}`.trim() : "Vehicle"}</span>
                      <small>{formatDateLabel(booking.startDate)} - {formatDateLabel(booking.endDate)}</small>
                      <span className="badge badge-pending">{booking.status}</span>
                    </article>
                  )) : (
                    <div className="driver-calendar-empty">No assigned bookings for this driver.</div>
                  )}
                </aside>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
}
